// Shared "Run the Play" scope logic — the single source of truth for which
// reps a viewer can pull into the play, and which stages count as pursuable.
// Used by both the Run the Play page and the home-page badge so the two can
// never drift (drift between two copies of this logic was the original ASM bug).
//
// Real org linkage (verified against live team_roster, Jun 30 2026):
//   ASM  ← TLs via tl.asm_id;  ← RMs via rm.asm_id OR rm.team_lead_id ∈ {my TLs}
//   TL   ← RMs via rm.team_lead_id
//   RM   ← just themselves
//   exec ← everyone
// NOTE: TLs carry asm_id (NOT team_lead_id) to point at their ASM here.

import type { TeamMember } from "./types";

export const PURSUABLE_STAGES = [
  "initial_contact",
  "presentation_scheduled",
  "follow_up",
] as const;

export const STAGE_LABELS: Record<string, string> = {
  initial_contact: "Initial Contact",
  presentation_scheduled: "Presentation Scheduled",
  follow_up: "Presentation Follow-up",
};

export const ROLE_LABEL: Record<string, string> = {
  asm: "ASM",
  team_lead: "TL",
  restore_manager: "RM",
};

export const EXEC_ROLES = new Set([
  "admin",
  "sales_director",
  "qc",
  "roof_director",
  "director_of_operations",
]);

export interface PlayRep {
  id: string;
  name: string;
  role: string;
  you: boolean;
  /** True when this rep is visible via a day-pass crew (not hierarchy). */
  crew?: boolean;
}

interface ViewerLite {
  id: string;
  full_name: string;
  role: string;
}

/**
 * Resolve the ordered list of reps a viewer may include in the play.
 * Self is always first and always present (even if the viewer isn't in the
 * RM/TL/ASM roster, e.g. an exec/super-user). Roster should be active rows
 * for roles restore_manager/team_lead/asm.
 */
export function resolvePlayReps(
  viewer: ViewerLite,
  roster: TeamMember[],
  isExec: boolean,
): PlayRep[] {
  const asms = roster.filter((m) => m.role === "asm");
  const tls = roster.filter((m) => m.role === "team_lead");
  const rms = roster.filter((m) => m.role === "restore_manager");

  let visASMs: TeamMember[] = [];
  let visTLs: TeamMember[] = [];
  let visRMs: TeamMember[] = [];

  if (isExec) {
    visASMs = asms;
    visTLs = tls;
    visRMs = rms;
  } else if (viewer.role === "asm") {
    visTLs = tls.filter((t) => t.asm_id === viewer.id);
    const myTLIds = new Set(visTLs.map((t) => t.id));
    visRMs = rms.filter(
      (r) =>
        r.asm_id === viewer.id ||
        (r.team_lead_id != null && myTLIds.has(r.team_lead_id)),
    );
  } else if (viewer.role === "team_lead") {
    visRMs = rms.filter((r) => r.team_lead_id === viewer.id);
  }
  // restore_manager → just self (lists stay empty)

  const self: PlayRep = {
    id: viewer.id,
    name: viewer.full_name,
    role: viewer.role,
    you: true,
  };
  const seen = new Set<string>([viewer.id]);
  const out: PlayRep[] = [self];
  for (const m of [...visASMs, ...visTLs, ...visRMs]) {
    if (seen.has(m.id)) continue;
    seen.add(m.id);
    out.push({ id: m.id, name: m.full_name, role: m.role, you: false });
  }
  return out;
}
